"use client";

import * as React from "react";
import { useRef, useState, useCallback } from "react";
import { cn } from "@repo/utils/cn";
import { SearchField, SearchFieldProps } from "@repo/ui-parkmobile/SearchField";
import { trackSearchLocation } from "@/utils/analytics";
import {
  useLocationAutocomplete,
  PlacePrediction,
} from "@/hooks/useLocationAutocomplete";

export type { PlacePrediction };

export interface SearchFieldWithAutocompleteProps
  extends Omit<SearchFieldProps, "onSearch" | "searchHref" | "onSearchClick"> {
  onPlaceSelect?: (place: PlacePrediction) => void;
  onSearch?: (value: string) => void;
  searchOptions?: {
    types?: string[];
    componentRestrictions?: google.maps.places.ComponentRestrictions;
  };
  sectionName?: string;
  analyticsEvent?: "search_location" | "";
  getSearchHref?: (place: PlacePrediction | null, inputValue: string) => string | undefined;
}

export function SearchFieldWithAutocomplete({
  onPlaceSelect,
  onSearch,
  searchOptions = {},
  className,
  sectionName,
  analyticsEvent,
  getSearchHref,
  ...props
}: SearchFieldWithAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedPlace, setSelectedPlace] = useState<PlacePrediction | null>(null);
  const predictionsCountRef = useRef<number>(0);

  const {
    inputValue,
    predictions,
    showDropdown,
    selectedIndex,
    isLoaded,
    handleInputChange,
    handleKeyDown: hookHandleKeyDown,
    handlePlaceSelect: hookHandlePlaceSelect,
    closeDropdown,
    containerRef,
  } = useLocationAutocomplete({
    searchOptions: {
      types: searchOptions.types,
      componentRestrictions: searchOptions.componentRestrictions,
    },
    onPlaceSelect: (place) => {
      predictionsCountRef.current = predictions.length;
      setSelectedPlace(place);
      onPlaceSelect?.(place);
    },
    onSearch,
  });

  const searchHref = getSearchHref
    ? getSearchHref(selectedPlace, inputValue)
    : undefined;

  const handleSearchClick = useCallback(() => {
    if (analyticsEvent === "search_location" && sectionName) {
      trackSearchLocation({
        section: sectionName,
        config: "searchField",
        search_text: inputValue,
        results_count: predictionsCountRef.current,
      });
    }
  }, [analyticsEvent, sectionName, inputValue]);

  const handleSearch = useCallback(
    (value: string) => {
      handleSearchClick();
      if (onSearch) {
        onSearch(value);
      }
      closeDropdown();
    },
    [handleSearchClick, onSearch, closeDropdown]
  );

  const handleInputChangeWithClear = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSelectedPlace(null);
      handleInputChange(e);
    },
    [handleInputChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && searchHref) {
        handleSearchClick();
      }
      hookHandleKeyDown(e);
    },
    [hookHandleKeyDown, searchHref, handleSearchClick]
  );

  return (
    <div ref={containerRef} className="uipm:relative uipm:w-full">
      <SearchField
        ref={inputRef}
        value={inputValue}
        onChange={handleInputChangeWithClear}
        onKeyDown={handleKeyDown}
        onSearch={handleSearch}
        searchHref={searchHref}
        onSearchClick={handleSearchClick}
        className={className}
        {...props}
      />

      {isLoaded && showDropdown && predictions.length > 0 && (
        <div
          className={cn(
            "uipm:absolute uipm:z-[9999] uipm:w-full",
            "uipm:mt-2 uipm:rounded-xl",
            "uipm:bg-[var(--surface-surface-primary)] uipm:shadow-lg",
            "uipm:max-h-[400px] uipm:overflow-y-auto",
            "uipm:scrollbar-thin uipm:scrollbar-thumb-gray-300 uipm:scrollbar-track-transparent",
            "hover:uipm:scrollbar-thumb-gray-400"
          )}
          style={{
            scrollbarWidth: "thin",
            scrollbarColor: "rgb(209 213 219) transparent",
          }}
        >
          {predictions.map((prediction, index) => (
            <button
              key={prediction.place_id}
              type="button"
              onClick={() => hookHandlePlaceSelect(prediction)}
              className={cn(
                "uipm:w-full uipm:px-4 uipm:py-3",
                "uipm:text-left uipm:transition-colors uipm:cursor-pointer",
                "uipm:hover:bg-[var(--surface-surface-secondary)]",
                selectedIndex === index &&
                  "uipm:bg-[var(--surface-surface-secondary)]"
              )}
            >
              <div className="uipm:font-medium uipm:text-[var(--typography-colors-text-faded-dark)]">
                {prediction.structured_formatting.main_text}
              </div>
              {prediction.structured_formatting.secondary_text && (
                <div className="uipm:text-sm uipm:text-[var(--typography-colors-text-faded-dark)]">
                  {prediction.structured_formatting.secondary_text}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
